import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { useSegment } from './useAnalytics';
import { logger } from '../utils/logger';
import {
  SENTRY_TRACKING_CONFIG_KEY,
  SENTRY_TRACKING_LEVEL_KEY,
  SENTRY_TRACKING_UPDATED_AT_KEY,
  SENTRY_SESSION_ID_KEY,
  SESSION_USER_ID_KEY,
} from '../utils/sentryTrackingConfig';

export const USER_TOKEN_NAME = 'authToken';
const PARENT_TOKEN_NAME = 'parentAuthToken';
const PARENT_EMAIL = 'parentEmail';
const PARENT_ACCESS_ID = 'parentAccessId';
export const HSA_TOKEN_NAME = 'hsaAuthToken';
const HEA_PASSWORD = 'heaPassword';
const HEA_LOCKACCOUNT = 'heaAccess';
const HEA_VISITPROFILE = 'heaVisitProfile';
const PARENT_FIRST_LOGIN = 'parentFirstLogin';
export const LOGIN_LOGGED_AT = 'lastLoginLoggedAt';
const FIRST_SHOW_RESET_LESSON = 'showFirstResetLesson';
export const SESSION_TIMED_OUT = 'sessionTimeOut';

const clearSentryTrackingConfig = () => {
  try {
    sessionStorage.removeItem(SENTRY_TRACKING_CONFIG_KEY);
    sessionStorage.removeItem(SENTRY_TRACKING_LEVEL_KEY);
    sessionStorage.removeItem(SENTRY_TRACKING_UPDATED_AT_KEY);
    sessionStorage.removeItem(SENTRY_SESSION_ID_KEY);
    sessionStorage.removeItem(SESSION_USER_ID_KEY);
  } catch (e) {
    logger.error('[Auth] Error clearing Sentry tracking config:', e);
  }
};

type ParentData = {
  email?: string;
  accessId?: string;
  schoolName?: string;
  schoolId?: string;
}

type AuthTokenContextType = {
  token: null | string;
  setAuthToken: (token: string, log?: boolean) => void;
  removeAuthToken: () => void;
  revokeUserSession: () => void | Promise<void>;
  parentToken: null | string;
  setParentAuthToken: (token: string) => void;
  removeParentAuthToken: () => void;
  parentData: ParentData | null;
  setParentData: (paData: ParentData | null) => void;
  hsaToken: null | string;
  setHsaAuthToken: (token: string) => void;
  removeHsaAuthToken: () => void;
  password: null | string;
  setAuthPassword: (password: string) => void;
  removeAuthPassword: () => void;
  lockAccount: null | string;
  setLockAccount: (lockAccount: string) => void;
  removeLockAccount: () => void
  visitProfile: null | string;
  setVisitProfile: (visitProfile: string) => void;
  removeVisitProfile: () => void
  parentFirstLogin: null | string;
  setParentFirstLogin: (visitProfile: string) => void;
  removeParentFirstLogin: () => void;
  logout: () => void;
  showFirstResetLesson: null | string;
  setShowFirstResetLesson: (firstResetLesson: string) => void;
  removeFirstResetLesson: () => void;
};

const initialType: AuthTokenContextType = {
  token: null,
  setAuthToken: (token: string, log?: boolean) => {},
  removeAuthToken: () => {},
  revokeUserSession: () => {},
  parentToken: null,
  setParentAuthToken: (token: string) => {},
  removeParentAuthToken: () => {},
  parentData: null,
  setParentData: (paData: ParentData | null) => {},
  hsaToken: null,
  setHsaAuthToken: (token: string) => {},
  removeHsaAuthToken: () => {},
  password: null,
  setAuthPassword: (password: string) => {},
  removeAuthPassword: () => {},
  lockAccount: null,
  setLockAccount: (lockAccount: string) => {},
  removeLockAccount: () => {},
  visitProfile: null,
  setVisitProfile: (visitProfile: string) => {},
  removeVisitProfile: () => {},
  parentFirstLogin: null,
  setParentFirstLogin: (parentFirstLogin: string) => {},
  removeParentFirstLogin: () => {},
  logout: () => {},
  showFirstResetLesson: null,
  setShowFirstResetLesson: (firstResetLesson: string) => {},
  removeFirstResetLesson: () => {},
};

const AuthTokenContext = createContext(initialType);

export const AuthTokenProvider = ({children}: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem(USER_TOKEN_NAME));
  const [parentToken, setParentToken] = useState<string | null>(localStorage.getItem(PARENT_TOKEN_NAME));
  const [hsaToken, setHsaToken] = useState<string | null>(localStorage.getItem(HSA_TOKEN_NAME));
  const [password, setPassword] = useState<string | null>(localStorage.getItem(HEA_PASSWORD));
  const [lockAccount, setAccountLock] = useState<string | null>(localStorage.getItem(HEA_LOCKACCOUNT));
  const [visitProfile, setVisitedProfile] = useState<string | null>(localStorage.getItem(HEA_VISITPROFILE));
  const [parentData, setParentInfo] = useState<ParentData | null>({email: localStorage.getItem(PARENT_EMAIL) || undefined, accessId: localStorage.getItem(PARENT_ACCESS_ID) || undefined});
  const [parentFirstLogin, setParentLogin] = useState<string | null>(localStorage.getItem(PARENT_FIRST_LOGIN));
  const [showFirstResetLesson, setFirstResetLesson] = useState<string | null>(localStorage.getItem(FIRST_SHOW_RESET_LESSON));
  const { segment } = useSegment();

  const setAuthToken = useCallback((newToken: string, log?: boolean) => {
    // Only update if the token has actually changed to prevent unnecessary re-renders
    const currentToken = localStorage.getItem(USER_TOKEN_NAME);
    if (currentToken === newToken) {
      // Token hasn't changed, only update login time if needed
      if (log) {
        localStorage.setItem(LOGIN_LOGGED_AT, `${Date.now()}`);
      }
      return;
    }
    
    setToken(newToken);
    localStorage.setItem(USER_TOKEN_NAME, newToken);
    if (log) {
      localStorage.setItem(LOGIN_LOGGED_AT, `${Date.now()}`);
    }
    document.dispatchEvent(new Event('storageChange'));
  }, []);

  const removeAuthToken = useCallback(() => {
    localStorage.removeItem(USER_TOKEN_NAME);
    localStorage.removeItem(PARENT_TOKEN_NAME);
    localStorage.removeItem(HSA_TOKEN_NAME);
    localStorage.removeItem(HEA_PASSWORD);
    localStorage.removeItem(HEA_LOCKACCOUNT);
    setToken(null);
    setHsaToken(null);
    setParentToken(null);
    document.dispatchEvent(new Event('storageChange'));
    segment?.reset();
  }, [segment]);

  const setParentAuthToken = useCallback((token: string) => {
    setParentToken(token);
    localStorage.setItem(PARENT_TOKEN_NAME, token);
    document.dispatchEvent(new Event('storageChange'));
  }, []);

  const removeParentAuthToken = useCallback(() => {
    setParentToken(null);
    setParentInfo(null);
    localStorage.removeItem(PARENT_TOKEN_NAME);
    localStorage.removeItem(PARENT_EMAIL);
    document.dispatchEvent(new Event('storageChange'));
    segment?.reset();
  }, [segment]);

  const setParentData = useCallback((paData: ParentData | null) => {
    if (paData && paData.email && paData.accessId) {
      setParentInfo(paData);
      localStorage.setItem(PARENT_EMAIL, paData.email);
      document.dispatchEvent(new Event('storageChange'));
    }
  }, []);

  const setHsaAuthToken = useCallback((token: string) => {
    setHsaToken(token);
    localStorage.setItem(HSA_TOKEN_NAME, token);
    document.dispatchEvent(new Event('storageChange'));
  }, []);

  const removeHsaAuthToken = useCallback(() => {
    setHsaToken(null);
    localStorage.removeItem(HSA_TOKEN_NAME);
    document.dispatchEvent(new Event('storageChange'));
    segment?.reset();
  }, [segment]);

  const setAuthPassword = useCallback((password: string) => {
    setPassword(password);
    localStorage.setItem(HEA_PASSWORD, password);
    document.dispatchEvent(new Event('storageChange'));
  }, [])

  const removeAuthPassword = useCallback(() => {
    setPassword(null);
    localStorage.removeItem(HEA_PASSWORD);
    document.dispatchEvent(new Event('storageChange'));
  }, []);

  const setLockAccount = useCallback((lock: string) => {
    setAccountLock(lock);
    localStorage.setItem(HEA_LOCKACCOUNT, lock);
    document.dispatchEvent(new Event('storageChange'));
  }, []);

  const removeLockAccount = useCallback(() => {
    setAccountLock(null);
    localStorage.removeItem(HEA_LOCKACCOUNT);
    document.dispatchEvent(new Event('storageChange'));
  }, []);

  const setVisitProfile = useCallback((visit: string) => {
    setVisitedProfile(visit);
    localStorage.setItem(HEA_VISITPROFILE, visit);
    document.dispatchEvent(new Event('storageChange'));
  }, []);

  const removeVisitProfile = useCallback(() => {
    setVisitedProfile(null);
    localStorage.removeItem(HEA_VISITPROFILE);
    document.dispatchEvent(new Event('storageChange'));
  },[])

  const setParentFirstLogin = useCallback((firstLogin: string) => {
    setParentLogin(firstLogin);
    localStorage.setItem(PARENT_FIRST_LOGIN, firstLogin);
    document.dispatchEvent(new Event('storageChange'));
  }, [])

  const removeParentFirstLogin = useCallback(() => {
    setParentLogin(null);
    localStorage.removeItem(PARENT_FIRST_LOGIN);
    document.dispatchEvent(new Event('storageChange'));
  }, [])

  const setShowFirstResetLesson = useCallback((firstResetLesson: string) => {
    setFirstResetLesson(firstResetLesson);
    localStorage.setItem(FIRST_SHOW_RESET_LESSON, firstResetLesson);
    document.dispatchEvent(new Event('storageChange'));
  }, [])

  const removeFirstResetLesson = useCallback(() => {
    setFirstResetLesson(null);
    localStorage.removeItem(FIRST_SHOW_RESET_LESSON);
    document.dispatchEvent(new Event('storageChange'));
  }, [])

  const onLocalStateChange = useCallback(event => {
    // eslint-disable-next-line eqeqeq
    if ((!event.storageArea || event.storageArea != localStorage) && event.type !== 'storageChange') return;
    setTimeout(() => {
      const newLockState = (localStorage.getItem(HEA_LOCKACCOUNT) || '').trim();
      if (lockAccount !== newLockState) {
        setAccountLock(newLockState);
      }
    }, 50);
  }, [lockAccount]);


  const revokeUserSession = useCallback(async () => {
    logger.warn('[Auth] Revoking user session', {
      hadToken: !!token,
      timestamp: new Date().toISOString()
    });
    
    localStorage.removeItem(USER_TOKEN_NAME);
    localStorage.removeItem(PARENT_TOKEN_NAME);
    localStorage.removeItem(HSA_TOKEN_NAME);
    localStorage.removeItem(HEA_PASSWORD);
    localStorage.removeItem(HEA_LOCKACCOUNT);
    setToken(null);
    setHsaToken(null);
    setParentToken(null);
    
    const timeoutValue = `${Date.now()}`;
    localStorage.setItem(SESSION_TIMED_OUT, timeoutValue);
    
    // Verify the flag was set correctly
    const verifyValue = localStorage.getItem(SESSION_TIMED_OUT);
    logger.warn('[Auth] Session timeout flag set', {
      expected: timeoutValue,
      actual: verifyValue,
      success: timeoutValue === verifyValue,
      timestamp: new Date().toISOString()
    });
    
    document.dispatchEvent(new Event('storageChange'));

    // session_ended is sent by the caller (e.g. useActivity) via trackEvent before revokeUserSession
    segment?.reset();
    clearSentryTrackingConfig();

    // Skip redirect for URL shortening app (which includes student mode)
    if (import.meta.env.VITE_URL_SHORTEN_APP !== "true") {
      // Redirect to login page after session revocation
      logger.warn('[Auth] Redirecting to login page after session revocation');
      window.location.href = '/login';
    }
  }, [segment, token]);

  const logout = useCallback(async () => {
    // logout/session tracking is centralized in useLoginMutation/logout flows
    removeAuthToken();
    removeHsaAuthToken();
    removeLockAccount();
    removeAuthPassword();
    removeVisitProfile();
    removeParentAuthToken();
    removeFirstResetLesson();
    clearSentryTrackingConfig();
    window.location.href = '/';
  }, [removeAuthPassword, removeAuthToken, removeHsaAuthToken, removeLockAccount, removeParentAuthToken, removeVisitProfile, removeFirstResetLesson]);

  useEffect(() => {
    document.addEventListener('storageChange', onLocalStateChange, false);
    window.addEventListener('storage', onLocalStateChange, false);

    return () => {
      document.removeEventListener('storageChange', onLocalStateChange);
      window.removeEventListener('storage', onLocalStateChange);
    };
  }, [onLocalStateChange]);

  return (
    <AuthTokenContext.Provider value={{
      token,
      setAuthToken,
      removeAuthToken,
      revokeUserSession,
      parentToken,
      setParentAuthToken,
      removeParentAuthToken,
      parentData,
      setParentData,
      hsaToken,
      setHsaAuthToken,
      removeHsaAuthToken,
      password,
      setAuthPassword,
      removeAuthPassword,
      lockAccount,
      setLockAccount,
      removeLockAccount,
      visitProfile,
      setVisitProfile,
      removeVisitProfile,
      parentFirstLogin,
      setParentFirstLogin,
      removeParentFirstLogin,
      logout,
      showFirstResetLesson,
      setShowFirstResetLesson,
      removeFirstResetLesson,
    }}>
      {children}
    </AuthTokenContext.Provider>
  );
}

export const useAuthToken = () => useContext(AuthTokenContext);
